import { ref } from 'vue';
import api from '@/services/api';

/**
 * Potwierdzenie usunięcia szablonu (plan usuwania szablonów, Etap 2).
 *
 * Ten sam wzorzec co `useConfirm`: obietnica + jedno okno montowane w App.vue.
 * Osobny mechanizm, bo pytanie jest inne — zwykłe potwierdzenie odpowiada
 * „tak/nie", a to zwraca WYBRANĄ DROGĘ: usunięcie z tokenem albo dezaktywację.
 *
 * Zależności i token pobiera serwer (`/templates/{rodzaj}/{id}/impact`).
 * Front ich nie liczy i nie generuje — token wyprodukowany w przeglądarce
 * byłby ozdobą, którą omija każde żądanie wysłane z pominięciem interfejsu.
 */
const state = ref(null);

export function useDestructiveConfirmDialog() {
    return state;
}

export function useDestructiveConfirm() {
    /**
     * @param {object} opcje
     * @param {string} opcje.rodzaj    — rodzaj szablonu (segment adresu API)
     * @param {string} opcje.id        — ULID szablonu
     * @param {string} opcje.nazwa     — nazwa pokazywana w oknie
     * @param {boolean} [opcje.mozeDezaktywowac] — czy pokazać drogę bezpieczną
     *
     * @returns {Promise<{akcja: 'usun', token: string|null} | {akcja: 'dezaktywuj'} | null>}
     *          null = użytkownik zamknął okno
     */
    async function potwierdzUsuniecie({ rodzaj, id, nazwa, mozeDezaktywowac = false }) {
        let wplyw = { zaleznosci: [], wymaga_tokenu: false, token: null };

        try {
            // skipErrorToast: okno pokazuje błąd samo, globalny toast byłby
            // drugą wiadomością o tym samym
            const { data } = await api.get(`/templates/${rodzaj}/${id}/impact`, { skipErrorToast: true });
            wplyw = data;
        } catch {
            // Brak wiedzy o zależnościach to nie powód, by blokować usuwanie —
            // ale użytkownik musi wiedzieć, że okno nie zna skutków.
            wplyw.bladWplywu = true;
        }

        return new Promise((resolve) => {
            state.value = { rodzaj, id, nazwa, mozeDezaktywowac, ...wplyw, resolve };
        });
    }

    /**
     * Potwierdzenie DEZAKTYWACJI szablonu w użyciu (Etap 4).
     *
     * Okno lżejsze od usuwania: bez tokenu, bo operacja jest odwracalna jednym
     * kliknięciem. Jego sednem jest zdanie, że dane już utworzone zostają
     * nietknięte — to główny powód, dla którego dezaktywacja jest drogą
     * bezpieczną, i to trzeba powiedzieć wprost.
     *
     * Gdy na szablonie nic nie wisi, okno się NIE pokazuje — wyłączenie
     * szablonu, z którego nic nie powstało, nie wymaga tłumaczenia.
     *
     * @param {string} opcje.coPrzestanieDzialac — skutek zależny od rodzaju;
     *        jeden ogólny komunikat byłby nieprawdziwy (sprawdzone w kodzie:
     *        audyt znika z listy wyboru, program blokuje zapisanie oddziału,
     *        program ewaluacyjny wstrzymuje przesyłanie ocen).
     * @returns {Promise<boolean>} czy kontynuować
     */
    async function potwierdzDezaktywacje({ rodzaj, id, nazwa, coPrzestanieDzialac }) {
        let zaleznosci = [];
        try {
            const { data } = await api.get(`/templates/${rodzaj}/${id}/impact`, { skipErrorToast: true });
            zaleznosci = data.zaleznosci ?? [];
        } catch {
            // Brak wiedzy nie może blokować operacji odwracalnej
            return true;
        }

        if (zaleznosci.length === 0) return true;

        return new Promise((resolve) => {
            state.value = {
                tryb: 'dezaktywacja',
                rodzaj, id, nazwa, coPrzestanieDzialac, zaleznosci,
                wymaga_tokenu: false, token: null,
                resolve: (wynik) => resolve(wynik?.akcja === 'dezaktywuj'),
            };
        });
    }

    return { potwierdzUsuniecie, potwierdzDezaktywacje };
}
